import type { CombinedError } from '@urql/core';

/**
 * Thrown when Contentful couldn't be reached (after retries). `handlePageLoadFailure` rethrows it so the
 * request fails with a 500 instead of being reported as a missing page (404).
 */
export class ContentfulUnavailableError extends Error {
	name = 'ContentfulUnavailableError';
}

const describeCause = (cause: unknown): string => {
	if (cause instanceof Error) {
		const { code } = cause as Error & { code?: string };
		return code ? `${code} ${cause.message}` : cause.message;
	}
	return typeof cause === 'string' ? cause : JSON.stringify(cause);
};

/**
 * Formats a urql error for logging. Node's fetch reports every connection-level failure as "fetch failed"
 * and keeps the real reason (ECONNRESET, EAI_AGAIN, UND_ERR_CONNECT_TIMEOUT, ...) on `cause`, so include it.
 */
export const describeContentfulError = (error: CombinedError | null | undefined): string => {
	const cause = (error?.networkError as (Error & { cause?: unknown }) | undefined)?.cause;
	if (cause === undefined || cause === null) {
		return String(error);
	}
	return `${String(error)} (cause: ${describeCause(cause)})`;
};

/**
 * Throws a `ContentfulUnavailableError` when a query failed outright (network failure, or an error with no
 * data such as a rate limit), so callers don't mistake the missing data for content that doesn't exist.
 */
export const throwIfContentfulUnavailable = (result: { error?: CombinedError; data?: unknown }, context: string) => {
	if (result.error && (result.error.networkError || !result.data)) {
		throw new ContentfulUnavailableError(`${context}: ${describeContentfulError(result.error)}`);
	}
};
